import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import Container from 'reactstrap/lib/Container';
import { getModelState } from 'client/data/luckdragon/model';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { SitemapModel, SitemapEntities, buildPagePath, buildVehicleSitemapPath } from 'client/data/models/sitemap';
import { CmsEntities, CmsModel, bindToContent } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { VehicleModel, buildMakeModelPath } from 'client/data/models/vehicle';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { getSitemapTitle, getBreadcrumbs } from 'site-modules/sitemap/utils/sitemap-helpers';
import { getStaticSitemapData } from 'site-modules/sitemap/utils/static-sitemaps-helper';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { ArrowLink } from 'site-modules/shared/components/arrow-link/arrow-link';
import { SitemapLinksList } from 'site-modules/sitemap/components/sitemap-links-list/sitemap-links-list';
import {
  VEHICLE_MAKES_SITEMAP_ROUTE,
  STATIC_SITEMAPS,
  CONTENT_SITEMAPS,
  CONTENT_SITEMAPS_ROUTES,
  ALPHABETICAL_SITEMAP_ROUTE,
  SITEMAP_CATEGORY_ROUTE,
} from 'client/site-modules/sitemap/constants/routes';

import './sitemap.scss';

export function SitemapUI({
  data = {
    results: [],
  },
  contentData = DEFAULT_CONTENT,
  location = {},
  makeSlug = '',
  modelSlug = '',
  makeName = '',
  modelName = '',
}) {
  const { pathname } = location;
  const results = get(data, 'results', []);

  const title = getSitemapTitle({ pathname, makeName, modelName });
  const breadcrumbs = getBreadcrumbs({ pathname, makeSlug, modelSlug, makeName, modelName });
  const links = STATIC_SITEMAPS.includes(pathname) ? getStaticSitemapData(pathname) : results;
  const categorizedLinks = contentData.children();

  return (
    <Container tag="main" id="main-content" tabIndex={-1} className="sitemap pb-3">
      <Breadcrumbs className="ps-0 mb-2" data={breadcrumbs} />
      <div className="d-md-flex align-items-center">
        <h1 className="heading-2">{title}</h1>
        {pathname === ALPHABETICAL_SITEMAP_ROUTE && (
          <ArrowLink to={SITEMAP_CATEGORY_ROUTE} className="ms-auto">
            Switch to the Categorized Sitemap
          </ArrowLink>
        )}
        {pathname === SITEMAP_CATEGORY_ROUTE && (
          <ArrowLink to={ALPHABETICAL_SITEMAP_ROUTE} className="ms-auto">
            Switch to Alphabetical Sitemap
          </ArrowLink>
        )}
      </div>
      <hr className="mt-1_5 mb-2" />
      <section className="sitemap-links">
        {!contentData.isDefault ? (
          categorizedLinks.map(entry => (
            <SitemapLinksList category={entry.title} links={entry.links()} pathname={pathname} key={entry.title} />
          ))
        ) : (
          <SitemapLinksList links={links} pathname={pathname} />
        )}
      </section>
    </Container>
  );
}

SitemapUI.propTypes = {
  contentData: CmsEntities.Content,
  data: SitemapEntities.Sitemap,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  makeSlug: PropTypes.string,
  modelSlug: PropTypes.string,
  modelName: PropTypes.string,
  makeName: PropTypes.string,
};

export const getSitemapDataPath = ({ location, makeSlug, modelSlug }) => {
  const pathname = get(location, 'pathname', '');

  if (STATIC_SITEMAPS.includes(pathname) || CONTENT_SITEMAPS_ROUTES.includes(pathname)) return null;

  if (pathname === VEHICLE_MAKES_SITEMAP_ROUTE) return 'makes';

  if (makeSlug) return buildVehicleSitemapPath({ make: makeSlug, model: modelSlug });

  return buildPagePath(pathname);
};

const getSitemapContentDataPath = ({ location }) => {
  const pathname = get(location, 'pathname', '');
  return CONTENT_SITEMAPS_ROUTES.includes(pathname) ? `${CONTENT_SITEMAPS[pathname]}&skipDli=true` : null;
};

export const stateToPropsConfig = {
  contentData: bindToContent(props => getSitemapContentDataPath(props)),
  data: bindToPath(props => getSitemapDataPath(props), SitemapModel),
  makeName: bindToPath(
    ({ makeSlug }) => makeSlug && 'makes',
    VehicleModel,
    (makes, { makeSlug }) => get(makes, `${makeSlug}.name`, '')
  ),
  modelName: bindToPath(
    ({ makeSlug, modelSlug }) => modelSlug && buildMakeModelPath({ make: makeSlug, model: modelSlug }),
    VehicleModel,
    model => get(model, 'name', '')
  ),
};

export const Sitemap = connectToModel(SitemapUI, stateToPropsConfig);

export const pageDefinitionConfiguration = {
  name: 'sitemap',
  category: 'sitemap',
  preload: (store, props) => {
    const preloader = new ModelPreloader(store);
    const make = get(props, 'match.params.make', '');
    const model = get(props, 'match.params.model', '');
    const pathname = get(props, 'location.pathname', '');

    if (CONTENT_SITEMAPS_ROUTES.includes(pathname)) {
      preloader.resolve(`content["${getSitemapContentDataPath(props)}"]`, CmsModel);
    } else {
      if (model) {
        preloader.resolve(buildMakeModelPath({ make, model }), VehicleModel);
      } else if (make) {
        preloader.resolve('makes', VehicleModel);
      }

      preloader.resolve(getSitemapDataPath({ ...props, makeSlug: make, modelSlug: model }), SitemapModel);
    }

    return preloader.load();
  },
  seo: (state, props) => {
    const modelState = getModelState(state);

    const pathname = get(props, 'location.pathname', '');
    const makeSlug = get(props, 'match.params.make', '');
    const modelSlug = get(props, 'match.params.model', '');
    let makeName;
    let modelName;

    if (modelSlug) {
      const model = modelState.get(buildMakeModelPath({ make: makeSlug, model: modelSlug }), VehicleModel);
      makeName = get(model, 'make.name');
      modelName = get(model, 'name');
    } else if (makeSlug) {
      makeName = get(modelState.get('makes', VehicleModel), `${makeSlug}.name`);
    }

    return {
      title: `${getSitemapTitle({ pathname, makeName, modelName })} | Edmunds`,
      description: `Comprehensive listing of all ${getSitemapTitle({
        pathname,
        makeName,
        modelName,
        withSitemapLabel: false,
      })} pages from Edmunds.com.`,
    };
  },
  component: props => (
    <Sitemap
      {...props}
      makeSlug={get(props, 'match.params.make', '')}
      modelSlug={get(props, 'match.params.model', '')}
    />
  ),
};

export default pageDefinition(pageDefinitionConfiguration);
